import { Button, Icon, IconSet, Layout, Tooltip } from '@postscript/components';
import React, { MouseEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { MediaQueries } from './MainLayout';

interface SwatchProps extends React.HTMLAttributes<HTMLElement> {
  contrastBorder?: boolean;
  core?: boolean;
  cssVar: string;
  hue: string;
  number: number;
}

interface SwatchColorProps {
  hex: string;
  core?: boolean;
  contrastBorder?: boolean;
}

const SwatchColor = styled.div<SwatchColorProps>`
  box-sizing: border-box;
  background: ${({ hex }) => hex};
  height: 0;
  width: 84%;
  padding-bottom: 84%;
  margin: 8%;
  border-radius: var(--border-radius-round);
  position: relative;
  cursor: pointer;
  transition: background-color 150ms, border-color 150ms, opacity 150ms,
    transform 150ms;
  transform-origin: center;
  border: ${({ contrastBorder }) =>
    contrastBorder ? `1px solid var(--border-color)` : ''};

  * {
    pointer-events: none;
  }
`;

const SwatchNumber = styled.div`
  color: var(--icon-indicator-color);
  font: var(--heading-xx-small);
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  opacity: 0;
  transition: opacity 250ms;
`;

const SwatchIcon = styled(Icon)`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: calc(50% - 12px);
  left: calc(50% - 12px);
  text-align: center;
  opacity: 1;
  transition: opacity 250ms;

  ${MediaQueries.phoneOnly} {
    --icon-size: 12px;
    top: calc(50% - 6px);
    left: calc(50% - 6px);
  }
`;

const SwatchButtonLayout = styled(Layout)`
  opacity: 0;
  transition: opacity 250ms;
`;

const SwatchWrapper = styled.div<{ hue: string; number: number }>`
  --icon-indicator-color: var(
    --${({ hue, number }) => (number > 5 ? `${hue}-1` : `${hue}-8`)}
  );
  --swatch-wrapper-size: 9.0909090909%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  flex: 1 0 var(--swatch-wrapper-size);
  max-width: var(--swatch-wrapper-size);
  position: relative;
  transition: opacity 250ms;

  &:hover {
    ${SwatchNumber},
    ${SwatchButtonLayout} {
      opacity: 1;
    }
    ${SwatchIcon} {
      opacity: 0;
    }
    ${SwatchColor} {
      transform: scale(1.08);
    }
  }

  ${SwatchColor}:active {
    transform: scale(1.06);
  }
`;

const copyColorValue = async (e: MouseEvent) => {
  try {
    await navigator.clipboard.writeText(
      `${(e.target as HTMLElement).dataset.copyvalue}`,
    );
    toast.success(`Copied: ${(e.target as HTMLElement).dataset.copyvalue}`);
  } catch (error) {
    toast.error('Could not copy color value :(');
  }
};

const Swatch = ({
  className,
  contrastBorder = false,
  core = false,
  cssVar,
  hue,
  number,
}: SwatchProps): JSX.Element => {
  const [hex, setHex] = useState('#ffffff');
  const [isMounted, setIsMounted] = useState(false);

  const getCssVariable = (varCode: string) => {
    return getComputedStyle(document.body).getPropertyValue(varCode);
  };

  useEffect(() => {
    setHex(getCssVariable(cssVar));
    setIsMounted(true);
  }, [cssVar]);

  return (
    <SwatchWrapper className={className} hue={hue} number={number}>
      <SwatchColor
        contrastBorder={contrastBorder}
        core={core}
        data-copyvalue={`var(${cssVar.trim()})`}
        hex={hex.trim()}
        onClick={copyColorValue}
      >
        <SwatchNumber>{number}</SwatchNumber>
        {core && (
          <SwatchIcon
            className="suggested-icon"
            color="var(--white)"
            component={IconSet.Bub}
            size={24}
          />
        )}
      </SwatchColor>
      <SwatchButtonLayout align="center" gap="var(--spacing-1)">
        <Button
          data-copyvalue={`var(${cssVar.trim()})`}
          data-for={`copy-${hue}-${number}-css-var-tooltip`}
          data-tip
          icon={IconSet.Code}
          onClick={copyColorValue}
          size="small"
          variant="text"
        />
        <Button
          data-copyvalue={hex.trim()}
          data-for={`copy-${hue}-${number}-hex-tooltip`}
          data-tip
          icon={IconSet.Hashtag}
          onClick={copyColorValue}
          size="small"
          variant="text"
        />
      </SwatchButtonLayout>
      {isMounted && (
        <>
          <Tooltip id={`copy-${hue}-${number}-css-var-tooltip`} size="small">
            Copy var({cssVar.trim()})
          </Tooltip>
          <Tooltip id={`copy-${hue}-${number}-hex-tooltip`} size="small">
            Copy {hex}
          </Tooltip>
        </>
      )}
    </SwatchWrapper>
  );
};

export default Swatch;
